/*---------------------------------------------------------------------------------------------
 * Copyright (c) Bentley Systems, Incorporated. All rights reserved.
 * See LICENSE.md in the project root for license terms and full copyright notice.
 *--------------------------------------------------------------------------------------------*/
import React, { useLayoutEffect, useRef } from 'react';
import { Tooltip, useTheme } from '@itwin/itwinui-react';
import './buttonGroup.css';

export type ButtonGroupProps = {
  /**
   * Buttons in the ButtonGroup. If buttons are wrapped in tooltips, there should be another wrapper
   * between a tooltip and the button such as span or div element.
   */
  children: JSX.Element[];
};

/**
 * Group buttons together for common actions
 * @example
 * <ButtonGroup>
 *   <IconButton>
 *     <SvgAdd />
 *   </IconButton>
 *   <IconButton>
 *     <SvgEdit />
 *   </IconButton>
 * </ButtonGroup>
 */
export const ButtonGroup = (props: ButtonGroupProps) => {
  const { children, ...rest } = props;
  useTheme();

  const buttonRefs = useRef<HTMLButtonElement[]>([]);

  const matchesFocusVisible = (element: HTMLElement | null) => {
    try {
      return element?.matches(':focus-visible');
    } catch {
      return false;
    }
  };

  useLayoutEffect(() => {
    if (buttonRefs.current.length > 0) {
      buttonRefs.current.forEach((button, index) => {
        const buttonCount = buttonRefs.current.length;
        const previous = index > 0 ? buttonRefs.current[index - 1] : null;
        button.classList.remove(
          'first-button',
          'last-button',
          'button-after-disabled',
          'button-after-enabled',
          'button-after-focus'
        );

        if (index === 0) {
          button.classList.add('first-button');
        } else if (index === buttonCount - 1) {
          button.classList.add('last-button');
        }

        if (previous?.disabled) {
          button.classList.add('button-after-disabled');
        } else if (previous) {
          button.classList.add('button-after-enabled');
        }

        if (
          previous?.matches(':hover') ||
          previous?.matches(':active') ||
          matchesFocusVisible(previous)
        ) {
          button.classList.add('button-after-focus');
        }
      });
    }
  });

  const newChildren: JSX.Element[] = [];
  React.Children.forEach(children, (child, index) => {
    if (child.type === Tooltip) {
      const div = React.Children.only(child.props.children);
      const button = React.Children.only(div.props.children);
      newChildren.push(
        React.cloneElement(child, {
          children: React.cloneElement(div, {
            children: React.cloneElement(button, {
              ref: (instance: any) => (buttonRefs.current[index] = instance),
            }),
          }),
        })
      );
    } else {
      newChildren.push(
        React.cloneElement(child, {
          ref: (instance: any) => (buttonRefs.current[index] = instance),
        })
      );
    }
  });
  return (
    <div className="button-group" {...rest}>
      {newChildren}
    </div>
  );
};
